import { Controller } from "@hotwired/stimulus";

// Each entry is a picked { file }, or a { signedId, filename } the Home box already uploaded.
export default class extends Controller {
  static targets = ["picker", "input", "list", "template"];
  static values = { max: Number };

  #entries = [];
  #disabled = false;

  connect() {
    this.inputTarget.form.addEventListener("direct-uploads:start", this.#lock);
    this.inputTarget.form.addEventListener("direct-uploads:end", this.#unlock);
    this.#render();
  }

  disconnect() {
    this.inputTarget.form.removeEventListener(
      "direct-uploads:start",
      this.#lock,
    );
    this.inputTarget.form.removeEventListener(
      "direct-uploads:end",
      this.#unlock,
    );
  }

  get count() {
    return this.#entries.length;
  }

  get files() {
    return this.#entries
      .filter((entry) => entry.file)
      .map((entry) => entry.file);
  }

  set disabled(value) {
    this.#disabled = value;
    this.pickerTarget.disabled = value;
    this.#render();
  }

  openPicker() {
    this.pickerTarget.click();
  }

  add() {
    const picked = Array.from(this.pickerTarget.files).map((file) => ({
      file,
    }));
    this.pickerTarget.value = "";
    this.#add(picked);
  }

  addUploaded(entries) {
    this.#add(entries);
  }

  remove({ params: { index } }) {
    if (this.#disabled) return;
    this.#entries.splice(index, 1);
    this.#changed();
  }

  #add(incoming) {
    if (this.#disabled) return;
    const room = this.maxValue - this.count;
    if (incoming.length > room) {
      window.alert(`You can attach up to ${this.maxValue} files per message.`);
    }
    this.#entries.push(...incoming.slice(0, room));
    this.#changed();
  }

  #changed() {
    this.#syncInput();
    this.#render();
    this.dispatch("change", { detail: { count: this.count } });
  }

  // Active Storage disables the input after a successful upload; an edit after a failed send re-arms it.
  #syncInput() {
    const transfer = new DataTransfer();
    this.files.forEach((file) => transfer.items.add(file));
    this.inputTarget.files = transfer.files;
    this.inputTarget.disabled = false;
    this.clearUploadedIds();
  }

  // Active Storage leaves finished uploads' signed ids beside the input when a later file fails.
  // Skips the blank placeholder Rails renders for a multiple file field.
  clearUploadedIds() {
    this.inputTarget.parentElement
      .querySelectorAll(
        `:scope > input[type=hidden][name="${this.inputTarget.name}"]`,
      )
      .forEach((hidden) => {
        if (hidden.value) hidden.remove();
      });
  }

  #lock = () => {
    this.disabled = true;
  };

  #unlock = () => {
    this.disabled = false;
  };

  #render() {
    this.listTarget.replaceChildren(
      ...this.#entries.map((entry, index) => this.#chip(entry, index)),
    );
    this.listTarget.hidden = this.count === 0;
  }

  #chip(entry, index) {
    const chip = this.templateTarget.content.firstElementChild.cloneNode(true);
    chip.querySelector("[data-attachment-name]").textContent =
      entry.file?.name ?? entry.filename;
    const remove = chip.querySelector("button");
    remove.disabled = this.#disabled;
    remove.setAttribute("data-admin--massimo--attachments-index-param", index);
    if (entry.signedId) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = this.inputTarget.name;
      hidden.value = entry.signedId;
      chip.append(hidden);
    }
    return chip;
  }
}
